import React, { useCallback, useMemo, useRef, useState, CSSProperties, useEffect } from 'react';
import cx from 'classnames';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  SortingState,
  ColumnDef,
} from '@tanstack/react-table';
import { useUpdateEffect } from 'ahooks';
import { NoDataFallback } from 'shared/components/legacy/NoDataFallback';
import { DataGridProps, GridRow, Column, DataGridFilters, ColumnFilter, DataGridDateFilter } from './types';
import { convertDataTreeToGridRows, isAtBottom, getRowHeight, isGridTitleRow } from './helpers/gridHelpers';
import classes from './DataGrid.module.scss';

const INDENT_PER_DEPTH = 20;

function ExpandIcon({ isExpanded, isLoading }: { isExpanded?: boolean; isLoading?: boolean }): JSX.Element {
  if (isLoading) {
    return <span className={classes.expandIcon}>...</span>;
  }
  return (
    <span className={cx(classes.expandIcon, { [classes.expanded]: isExpanded })}>
      &#9654;
    </span>
  );
}

function SelectionCheckbox({
  selectionState,
  onClick,
}: {
  selectionState?: boolean | 'intermediate';
  onClick: (e: React.MouseEvent) => void;
}): JSX.Element {
  return (
    <span
      className={cx(classes.selectionCheckbox, {
        [classes.checked]: selectionState === true,
        [classes.intermediate]: selectionState === 'intermediate',
      })}
      onClick={onClick}
    />
  );
}

function DateFilterPopup({
  hasTime,
  onApply,
  onClose,
  initialValue,
}: {
  hasTime?: boolean;
  onApply: (value: DataGridDateFilter['value'] | null) => void;
  onClose: () => void;
  initialValue?: DataGridDateFilter['value'] | null;
}): JSX.Element {
  const [period, setPeriod] = useState<'equal' | 'before' | 'after' | 'between'>(
    initialValue && 'period' in initialValue ? initialValue.period : 'equal',
  );
  const [date1, setDate1] = useState('');
  const [date2, setDate2] = useState('');

  const handleApply = () => {
    if (!date1) {
      onApply(null);
      onClose();
      return;
    }
    if (period === 'between') {
      if (!date2) return;
      onApply({
        date: [new Date(date1), new Date(date2)],
        period: 'between',
      });
    } else {
      onApply({
        date: new Date(date1),
        period,
      });
    }
    onClose();
  };

  const handleClear = () => {
    onApply(null);
    onClose();
  };

  return (
    <div className={classes.dateFilterPopup} onClick={(e) => e.stopPropagation()}>
      <select value={period} onChange={(e) => setPeriod(e.target.value as typeof period)}>
        <option value="equal">=</option>
        <option value="before">&lt;</option>
        <option value="after">&gt;</option>
        <option value="between">...</option>
      </select>
      <input
        type={hasTime ? 'datetime-local' : 'date'}
        value={date1}
        onChange={(e) => setDate1(e.target.value)}
      />
      {period === 'between' && (
        <input
          type={hasTime ? 'datetime-local' : 'date'}
          value={date2}
          onChange={(e) => setDate2(e.target.value)}
        />
      )}
      <div className={classes.dateFilterActions}>
        <button type="button" onClick={handleApply}>OK</button>
        <button type="button" onClick={handleClear}>X</button>
      </div>
    </div>
  );
}

// Multiselect Filter Popup Component
function MultiselectFilterPopup({
  options,
  onApply,
  onClose,
  initialValue,
}: {
  options: Array<{ value: string | number; label: string }>;
  onApply: (value: Array<string | number> | null) => void;
  onClose: () => void;
  initialValue?: Array<string | number> | null;
}): JSX.Element {
  const [selected, setSelected] = useState<Set<string | number>>(new Set(initialValue ?? []));

  const toggle = (val: string | number) => {
    const next = new Set(selected);
    if (next.has(val)) next.delete(val);
    else next.add(val);
    setSelected(next);
  };

  const handleApply = () => {
    onApply(selected.size > 0 ? Array.from(selected) : null);
    onClose();
  };

  return (
    <div className={classes.multiselectFilterPopup} onClick={(e) => e.stopPropagation()}>
      <div className={classes.multiselectOptions}>
        {options.map((opt) => (
          <label key={String(opt.value)} className={classes.multiselectOption}>
            <input
              type="checkbox"
              checked={selected.has(opt.value)}
              onChange={() => toggle(opt.value)}
            />
            <span>{opt.label}</span>
          </label>
        ))}
      </div>
      <div className={classes.dateFilterActions}>
        <button type="button" onClick={handleApply}>OK</button>
        <button type="button" onClick={() => { onApply(null); onClose(); }}>X</button>
      </div>
    </div>
  );
}

// Filter Cell Component
function FilterCell<DR>({
  column,
  filter,
  hasFilterValue,
  currentFilterValue,
  onFilterApply,
}: {
  column: Column<DR>;
  filter?: ColumnFilter;
  hasFilterValue: boolean;
  currentFilterValue: DataGridFilters[string] | undefined;
  onFilterApply: (filterKey: string, value: DataGridFilters[string]) => void;
}): JSX.Element | null {
  const [showPopup, setShowPopup] = useState(false);
  const filterKey = column.filterKey || (filter && 'name' in filter && filter.name) || column.key;

  if (!filter) return <div className={classes.filterCell} />;

  switch (filter.type) {
    case 'date':
      return (
        <div className={classes.filterCell} style={{ position: 'relative' }}>
          <button
            type="button"
            className={cx(classes.filterButton, { [classes.filterActive]: hasFilterValue })}
            onClick={() => setShowPopup(!showPopup)}
            aria-label="Filter by date"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
              <path d="M0 0l4.571 5.714v4l2.858 2.286V5.714L12 0H0z" />
            </svg>
          </button>
          {showPopup && (
            <DateFilterPopup
              hasTime={filter.hasTime}
              initialValue={currentFilterValue && currentFilterValue.type === 'date' ? currentFilterValue.value : null}
              onApply={(value) => {
                onFilterApply(filterKey, value ? { type: 'date', hasTime: filter.hasTime ?? false, value } : null);
              }}
              onClose={() => setShowPopup(false)}
            />
          )}
        </div>
      );

    case 'text':
      return (
        <div className={classes.filterCell}>
          <input
            type="text"
            className={classes.filterInput}
            placeholder="..."
            defaultValue={currentFilterValue?.type === 'text' ? currentFilterValue.value : ''}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const val = (e.target as HTMLInputElement).value;
                onFilterApply(filterKey, val ? { type: 'text', value: val } : null);
              }
            }}
          />
        </div>
      );

    case 'multiselect':
      return (
        <div className={classes.filterCell} style={{ position: 'relative' }}>
          <button
            type="button"
            className={cx(classes.filterButton, { [classes.filterActive]: hasFilterValue })}
            onClick={() => setShowPopup(!showPopup)}
            aria-label="Filter by selection"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
              <path d="M0 0l4.571 5.714v4l2.858 2.286V5.714L12 0H0z" />
            </svg>
          </button>
          {showPopup && (
            <MultiselectFilterPopup
              options={filter.options}
              initialValue={currentFilterValue?.type === 'multiselect' ? currentFilterValue.value : null}
              onApply={(value) => {
                onFilterApply(filterKey, value ? { type: 'multiselect', value: value as number[] } : null);
              }}
              onClose={() => setShowPopup(false)}
            />
          )}
        </div>
      );

    default:
      return <div className={classes.filterCell} />;
  }
}

function DataGridV2<DR>(props: DataGridProps<DR>): JSX.Element {
  const {
    isLoading = false,
    isLoadedAll = true,
    loadMore,
    selectedRowKeys = new Set<string>(),
    rows,
    columns,
    scrollThreshold = 100,
    headerRowHeight = 50,
    filterRowHeight = 0,
    dataRowHeight = 50,
    titleRowHeight = 35,
    rowGap = 0,
    rowKeyGetter = (row: GridRow<DR>): string => row.rowId,
    gridRef,
    className,
    enableVirtualization = true,
    autoScrollTop = true,
    initialFilters,
    initialSorting,
    onSortingChange,
    onFilterChange,
    toggleRowSelection,
    toggleRowExpanding,
    applyFilters,
  } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const [sorting, setSorting] = useState<SortingState>(
    initialSorting ? [{ id: initialSorting.columnKey, desc: initialSorting.direction === 'desc' }] : [],
  );
  const [filters, setFilters] = useState<DataGridFilters>(initialFilters || {});

  const gridRows = useMemo(() => convertDataTreeToGridRows(rows, columns), [rows, columns]);

  const titleRowTemplateColumns = useMemo(
    () => columns.filter((col) => col.titleRowTemplates && col.titleRowTemplates.length > 0),
    [columns],
  );

  const handleFilterApply = useCallback(
    (filterKey: string, value: DataGridFilters[string]) => {
      const doApply = () => {
        const newFilters = { ...filters };
        if (value === null || value === undefined) {
          delete newFilters[filterKey];
        } else {
          newFilters[filterKey] = value;
        }
        setFilters(newFilters);
        onFilterChange?.(newFilters);
      };

      if (applyFilters) {
        const target = document.querySelector(`[data-filter-key="${filterKey}"]`) as HTMLElement;
        if (target) {
          applyFilters(doApply, target);
        } else {
          doApply();
        }
      } else {
        doApply();
      }
    },
    [filters, onFilterChange, applyFilters],
  );

  // TanStack Table columns definition
  const tableColumns = useMemo<ColumnDef<GridRow<DR>>[]>(() => {
    return columns.map((column, index): ColumnDef<GridRow<DR>> => {
      return {
        id: column.key || `col-${index}`,
        accessorFn: (row) => {
          if (column.valueGetter) {
            return column.valueGetter(row.data);
          }
          return row.data[column.key as keyof DR];
        },
        header: column.name,
        size: typeof column.width === 'number' ? column.width : 150,
        minSize: column.minWidth || 60,
        maxSize: column.maxWidth || undefined,
        enableSorting: column.isSortable ?? false,
        enableResizing: column.resizable ?? false,
        cell: ({ row, cell }) => {
          const gridRow = row.original;

          // Use custom formatter if provided
          if (column.formatter) {
            return column.formatter({
              row: gridRow,
              column: column as unknown as Column<GridRow<DR>>,
              rowIdx: row.index,
              isCellEditable: false,
              tabIndex: 0,
              onRowChange: () => {},
            });
          }

          // Default cell rendering
          const value = cell.getValue();
          return <span>{value != null ? String(value) : ''}</span>;
        },
      };
    });
  }, [columns]);

  // TanStack Table instance
  const table = useReactTable({
    data: gridRows,
    columns: tableColumns,
    state: {
      sorting,
    },
    onSortingChange: (updater) => {
      const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
      setSorting(newSorting);

      if (onSortingChange && newSorting.length > 0) {
        onSortingChange({
          columnKey: newSorting[0].id,
          direction: newSorting[0].desc ? 'desc' : 'asc',
        });
      } else if (onSortingChange) {
        onSortingChange(null);
      }
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getRowId: rowKeyGetter,
  });

  // Virtualization setup
  const tableRows = table.getRowModel().rows;

  const rowVirtualizer = useVirtualizer({
    count: tableRows.length,
    getScrollElement: () => containerRef.current,
    estimateSize: useCallback(
      (index: number) => {
        const row = tableRows[index]?.original;
        if (!row) return dataRowHeight;
        return getRowHeight(row, dataRowHeight, titleRowHeight, rowGap);
      },
      [tableRows, dataRowHeight, titleRowHeight, rowGap],
    ),
    overscan: 5,
    enabled: enableVirtualization,
  });

  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();

  // Calculate total width of all columns for horizontal scrolling
  const totalColumnsWidth = useMemo(() => {
    return table.getHeaderGroups().reduce((total, headerGroup) => {
      return headerGroup.headers.reduce((sum, header) => {
        return sum + header.getSize();
      }, 0);
    }, 0);
  }, [table]);

  // Sync horizontal scroll between header and body
  const handleBodyScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const target = event.currentTarget;

      // Sync header scroll with body
      if (headerRef.current) {
        headerRef.current.scrollLeft = target.scrollLeft;
      }

      // Handle infinite loading
      if (!isLoading && !isLoadedAll && loadMore) {
        if (isAtBottom(event, scrollThreshold)) {
          loadMore();
        }
      }
    },
    [isLoading, isLoadedAll, loadMore, scrollThreshold],
  );

  // CSS variables for heights
  const cssVariables = useMemo(
    () =>
      ({
        '--filter-row-height': `${filterRowHeight}px`,
        '--header-row-height': `${headerRowHeight}px`,
        '--title-row-height': `${titleRowHeight}px`,
        '--data-row-height': `${dataRowHeight}px`,
      } as CSSProperties),
    [filterRowHeight, headerRowHeight, titleRowHeight, dataRowHeight],
  );

  // Auto scroll to top when data changes
  useUpdateEffect(() => {
    if (autoScrollTop && gridRows.length && containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  }, [autoScrollTop, gridRows]);

  // Native scroll listener for better performance and reliability
  useEffect(() => {
    const bodyEl = containerRef.current;
    const headerEl = headerRef.current;
    if (!bodyEl || !headerEl) return;

    const handleNativeScroll = () => {
      headerEl.scrollLeft = bodyEl.scrollLeft;
    };

    bodyEl.addEventListener('scroll', handleNativeScroll, { passive: true });
    return () => {
      bodyEl.removeEventListener('scroll', handleNativeScroll);
    };
  }, []);

  // Expose gridRef
  useEffect(() => {
    if (gridRef && containerRef.current) {
      const element = containerRef.current as unknown as HTMLElement & {
        scrollToCell: (options: { rowIdx: number }) => void;
      };
      element.scrollToCell = (options: { rowIdx: number }) => {
        rowVirtualizer.scrollToIndex(options.rowIdx, { align: 'start' });
      };

      (
        gridRef as React.MutableRefObject<{
          clearFilters(): void;
          filters: DataGridFilters;
          nativeGridRef: React.RefObject<HTMLElement>;
        } | null>
      ).current = {
        clearFilters: () => {
          setFilters({});
          table.resetColumnFilters();
          onFilterChange?.({});
        },
        filters,
        nativeGridRef: containerRef as React.RefObject<HTMLElement>,
      };
    }
  }, [gridRef, table, filters, rowVirtualizer, onFilterChange]);

  const hasFilters = filterRowHeight > 0 && columns.some((col) => col.filter);

  // Handle expand click
  const handleExpandClick = useCallback(
    (e: React.MouseEvent, rowId: string) => {
      e.stopPropagation();
      toggleRowExpanding?.(rowId);
    },
    [toggleRowExpanding],
  );

  // Handle selection click
  const handleSelectionClick = useCallback(
    (e: React.MouseEvent, rowId: string) => {
      e.stopPropagation();
      toggleRowSelection?.(rowId);
    },
    [toggleRowSelection],
  );

  // Render title row content
  const renderTitleRow = (gridRow: GridRow<DR>) => {
    const depth = gridRow.depth ?? 0;
    const indent = depth * INDENT_PER_DEPTH;

    return (
      <div className={classes.titleRowContent} style={{ paddingLeft: indent }}>
        {/* Expand icon */}
        {toggleRowExpanding && (
          <span
            className={classes.expandToggle}
            onClick={(e) => handleExpandClick(e, gridRow.row.rowId)}
          >
            <ExpandIcon isExpanded={gridRow.row.isExpanded} isLoading={gridRow.row.isLoading} />
          </span>
        )}
        {/* Selection checkbox */}
        {toggleRowSelection && (
          <SelectionCheckbox
            selectionState={gridRow.row.selectionState}
            onClick={(e) => handleSelectionClick(e, gridRow.row.rowId)}
          />
        )}
        {/* Title content from templates */}
        {titleRowTemplateColumns.map((col) =>
          col.titleRowTemplates?.map((template, idx) => (
            <React.Fragment key={`${col.key}-${idx}`}>{template(gridRow)}</React.Fragment>
          )),
        )}
      </div>
    );
  };

  const renderCellContent = (
    gridRow: GridRow<DR>,
    cellContent: React.ReactNode,
    colIndex: number,
  ) => {
    const column = columns[colIndex];
    if (!column) return cellContent;

    const depth = gridRow.depth ?? 0;
    const hasExpandOrSelect = column.isExpandColumn || column.isSelectColumn;
    const indent = column.hasIndent ? depth * INDENT_PER_DEPTH : 0;

    if (!hasExpandOrSelect && indent === 0) return cellContent;

    return (
      <div className={classes.cellWithControls} style={indent > 0 ? { paddingLeft: indent } : undefined}>
        {column.isExpandColumn && gridRow.row.children !== undefined && (
          <span
            className={classes.expandToggle}
            onClick={(e) => handleExpandClick(e, gridRow.row.rowId)}
          >
            <ExpandIcon isExpanded={gridRow.row.isExpanded} isLoading={gridRow.row.isLoading} />
          </span>
        )}
        {column.isSelectColumn && toggleRowSelection && (
          <SelectionCheckbox
            selectionState={gridRow.row.selectionState}
            onClick={(e) => handleSelectionClick(e, gridRow.row.rowId)}
          />
        )}
        <span className={classes.cellValue}>{cellContent}</span>
      </div>
    );
  };

  const renderRowContent = (row: (typeof tableRows)[0], style?: React.CSSProperties) => {
    const gridRow = row.original;
    const isTitleRow = isGridTitleRow(gridRow);
    const originalRowId = gridRow.row.rowId;

    if (isTitleRow) {
      return (
        <div
          key={row.id}
          className={cx(classes.row, classes.titleRow, {
            [classes.selected]: selectedRowKeys?.has(originalRowId),
          })}
          style={style}
          onClick={() => toggleRowExpanding?.(originalRowId)}
        >
          <div className={classes.titleRowCell} style={{ width: totalColumnsWidth || '100%' }}>
            {renderTitleRow(gridRow)}
          </div>
        </div>
      );
    }

    return (
      <div
        key={row.id}
        className={cx(classes.row, {
          [classes.selected]: selectedRowKeys?.has(originalRowId),
          [classes.depthRow]: (gridRow.depth ?? 0) > 0,
        })}
        style={style}
      >
        {row.getVisibleCells().map((cell, cellIndex) => (
          <div
            key={cell.id}
            className={classes.cell}
            style={{
              width: cell.column.getSize(),
              minWidth: cell.column.columnDef.minSize,
              maxWidth: cell.column.columnDef.maxSize,
              justifyContent: columns[cellIndex]?.textAlign === 'left' ? 'flex-start' : 'center',
            }}
          >
            {renderCellContent(
              gridRow,
              flexRender(cell.column.columnDef.cell, cell.getContext()),
              cellIndex,
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={cx(classes.root, className)} style={cssVariables}>
      <div className={classes.container}>
        {/* Header - scrolls horizontally in sync with body */}
        <div
          ref={headerRef}
          className={classes.headerWrapper}
          style={{ height: headerRowHeight + (hasFilters ? filterRowHeight : 0) }}
        >
          <div className={classes.header} style={{ width: totalColumnsWidth || '100%', minWidth: '100%' }}>
            {/* Header Row */}
            {table.getHeaderGroups().map((headerGroup) => (
              <div key={headerGroup.id} className={classes.headerRow}>
                {headerGroup.headers.map((header) => (
                  <div
                    key={header.id}
                    className={cx(classes.headerCell, {
                      [classes.sortable]: header.column.getCanSort(),
                    })}
                    style={{
                      width: header.getSize(),
                      minWidth: header.column.columnDef.minSize,
                      maxWidth: header.column.columnDef.maxSize,
                    }}
                    onClick={header.column.getToggleSortingHandler()}
                    title={String(header.column.columnDef.header)}
                  >
                    <span>{flexRender(header.column.columnDef.header, header.getContext())}</span>
                    {header.column.getIsSorted() && (
                      <span className={classes.sortIndicator}>
                        {header.column.getIsSorted() === 'asc' ? '▲' : '▼'}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ))}

            {/* Filter Row */}
            {hasFilters && (
              <div className={classes.filterRow} data-testid="filter-row" style={{ display: 'flex' }}>
                {columns.map((column, index) => {
                  const header = table.getHeaderGroups()[0]?.headers[index];
                  if (!header) return null;
                  const filterKey =
                    column.filterKey || (column.filter && 'name' in column.filter && column.filter.name) || column.key;

                  return (
                    <div
                      key={column.key || `filter-${index}`}
                      className={classes.filterCell}
                      style={{
                        width: header.getSize(),
                        minWidth: header.column.columnDef.minSize,
                        maxWidth: header.column.columnDef.maxSize,
                      }}
                      data-filter-key={filterKey}
                    >
                      <FilterCell
                        column={column}
                        filter={column.filter}
                        hasFilterValue={!!filters[filterKey]}
                        currentFilterValue={filters[filterKey]}
                        onFilterApply={handleFilterApply}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Body with virtualization */}
        <div
          ref={containerRef}
          className={classes.body}
          onScroll={handleBodyScroll}
          style={{
            height: `calc(100% - ${headerRowHeight + (hasFilters ? filterRowHeight : 0)}px)`,
          }}
        >
          {enableVirtualization ? (
            <div
              style={{
                height: `${totalSize}px`,
                position: 'relative',
                width: totalColumnsWidth || '100%',
                minWidth: '100%',
              }}
            >
              {virtualRows.map((virtualRow) => {
                const row = tableRows[virtualRow.index];
                if (!row) return null;

                return renderRowContent(row, {
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  height: `${virtualRow.size}px`,
                  transform: `translateY(${virtualRow.start}px)`,
                });
              })}
            </div>
          ) : (
            <div
              style={{
                width: totalColumnsWidth || '100%',
                minWidth: '100%',
              }}
            >
              {tableRows.map((row) => renderRowContent(row))}
            </div>
          )}

          {isLoading && <div className={classes.loadingIndicator}>Loading...</div>}

          {!isLoading && gridRows.length === 0 && (
            <div className={classes.noDataContainer}>
              <NoDataFallback />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export const DataGrid = React.memo(DataGridV2) as typeof DataGridV2;
export * from './types';
export * from './helpers/gridHelpers';
